import { StructuredText } from 'react-datocms';
import { dumbPluralize } from '@/utils/helpers';
import { StyledWell } from '@/components/StyledWell';
import type { IsaRatesTableFragment } from '@/utils/dato/generated';
import { customNodeRules, renderBlock } from '@/components/structuredText';
import { isEmptyDocument, type Document } from 'datocms-structured-text-utils';
import { usePotOptionsQuery, PotType, Product, Group } from '@/utils/savings-easy-access/generated';

const IsaRatesTable = ({ showEasyAccessSection = true, content, title }: IsaRatesTableFragment) => {
  const { data } = usePotOptionsQuery();
  const pots = data?.maximumPotRatesWithBonusOffers.filter(({ group }) => group === Group.ISA);
  const easyAccessPot = pots?.find(({ potType }) => potType === PotType.ISA_ACCESS);
  const fixedTermPots = pots?.filter(({ product }) => product === Product.FIXED);

  return pots ? (
    <StyledWell>
      {title ? <h3 className="text-4xl lg:text-5xl font-serif font-semibold mb-7">{title}</h3> : null}
      {showEasyAccessSection ? (
        <div>
          <div className="flex justify-center items-center mb-4">
            <h4 className="text-center lg:text-lg font-semibold">Access ISA pots</h4>

            <span className="material-symbols-outlined ml-2 text-success-content" aria-hidden="true">
              lock_open_right
            </span>
          </div>

          {easyAccessPot ? (
            <div className="overflow-hidden rounded-lg border border-base-100">
              <div className="grid grid-cols-3 py-6 px-4 font-semibold text-center">
                <span>AER*</span>

                <span>Gross**</span>

                <span>Access money</span>
              </div>

              <div className="grid grid-cols-3 bg-base-100 py-6 px-4 text-center">
                <span>{Number(easyAccessPot.interestRateAER).toFixed(2)}%</span>

                <span>{Number(easyAccessPot.interestRateGross).toFixed(2)}%</span>

                <span>Access Anytime</span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {fixedTermPots?.length ? (
        <div className="mt-8">
          <div className="flex justify-center items-center mb-4">
            <h4 className="text-center lg:text-lg font-semibold"> Fixed Term ISA pots</h4>
            <span className="material-symbols-outlined ml-2 text-success-content" aria-hidden="true">
              schedule
            </span>
          </div>

          <div className="overflow-hidden rounded-lg border border-base-100">
            <div className="grid grid-cols-3 py-6 px-4 font-semibold text-center">
              <span>AER*</span>

              <span>Gross**</span>

              <span>Access money</span>
            </div>

            {fixedTermPots.map((rate, index) => (
              <div
                className={`grid grid-cols-3 py-6 px-4 text-center ${index % 2 ? 'bg-base-100' : 'bg-white'}`}
                key={index}
              >
                <span>{Number(rate.interestRateAER).toFixed(2)}%</span>
                <span>{Number(rate.interestRateGross).toFixed(2)}%</span>
                {rate.lockPeriodInDays ? (
                  <span>
                    {rate.lockPeriodInDays / 365} {dumbPluralize(rate.lockPeriodInDays / 365, 'year')} term
                  </span>
                ) : (
                  <span>-</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {content?.value && !isEmptyDocument(content?.value) ? (
        <div className="mt-6 max-w-2xl mx-auto px-4">
          <StructuredText
            data={{ value: content?.value as Document, blocks: content?.blocks }}
            customNodeRules={customNodeRules}
            renderBlock={renderBlock}
          />
        </div>
      ) : null}
    </StyledWell>
  ) : null;
};

export default IsaRatesTable;
